import { computed } from 'vue'
import type { ComputedRef, Ref } from 'vue'

export type NavGridTier = 'phone' | 'tabletPortrait' | 'tabletLandscape' | 'web'
export type NavRailOrientation = 'horizontal' | 'vertical'
export type NavRailVariant = 'compact' | 'expanded'

export interface NavGridLayout {
  tier: NavGridTier
  // Column count for the full nav overlay grid (all 19 items).
  columns: number
  // CSS width of the full nav overlay drawer. A CSS-length string (not a
  // fixed px number) so phone can use a viewport-relative width per
  // DEVAMS-2670 review feedback (the first pass's fixed 260px drawer was
  // too narrow — labels wrapped/squeezed).
  overlayWidth: string
  // DEVAMS-2670 second-round review: phone no longer renders the
  // always-visible rail at all — its favorites now live in the pre-existing
  // bottom tab bar (Footer.vue) instead, which is a separate component.
  showRail: boolean
  // Always 'vertical' now that phone (the only tier that used 'horizontal')
  // no longer renders a rail. Kept as a field (rather than hardcoding
  // 'vertical' at every call site) in case a future tier needs otherwise.
  railOrientation: NavRailOrientation
  // Rail inline-size in px.
  railSize: number
  // 'compact': tablet (portrait + landscape) — icon stacked above a small
  // label.
  // 'expanded': web — wider "folded out" mini-drawer, icon+label side by
  // side per row.
  // DEVAMS-2670 fifth-round review: every rail-bearing tier now renders its
  // own embedded toggle button as the FIRST item at the top of the rail's
  // icon column (above "Dashboard") — the trailing "More…" row (web-only,
  // bottom of the list) was dropped because it duplicated the toggle and
  // wasn't visible without scrolling.
  railVariant: NavRailVariant
  // Whether the rail renders its own embedded toggle button.
  railShowToggle: boolean
}

const TIERS: Record<NavGridTier, NavGridLayout> = {
  phone: {
    tier: 'phone', columns: 4, overlayWidth: '88vw',
    showRail: false, railOrientation: 'horizontal', railSize: 0,
    railVariant: 'compact', railShowToggle: false,
  },
  tabletPortrait: {
    tier: 'tabletPortrait', columns: 5, overlayWidth: '480px',
    // DEVAMS-2670 third-round review: widened from 80 -> 108px so item
    // labels ("Dashboard", "Your roster", etc.) can fit or wrap onto two
    // lines instead of being ellipsis-truncated (see
    // .nav-favorites-rail-item-label in NavFavoritesRail.vue).
    showRail: true, railOrientation: 'vertical', railSize: 108,
    railVariant: 'compact', railShowToggle: true,
  },
  tabletLandscape: {
    tier: 'tabletLandscape', columns: 6, overlayWidth: '560px',
    // Shares the compact rail styling with tabletPortrait — same fix applies.
    showRail: true, railOrientation: 'vertical', railSize: 108,
    railVariant: 'compact', railShowToggle: true,
  },
  web: {
    tier: 'web', columns: 6, overlayWidth: '620px',
    showRail: true, railOrientation: 'vertical', railSize: 176,
    railVariant: 'expanded', railShowToggle: true,
  },
}

/**
 * DEVAMS-2670 (second-round review): the previous single 'wide' tier
 * (>=960px) collapsed iPad landscape and web into one bucket, but review
 * feedback treats them differently (tablet landscape keeps the compact
 * rail; web gets a wider expanded rail). Split at 1100px — chosen because it
 * sits between the ~1024px iPad-landscape verification viewport and the
 * ~1180px web verification viewport, so both land in their intended tier.
 *
 * DEVAMS-2670 (fifth-round review): every rail-bearing tier's own embedded
 * toggle button is the SOLE control that opens the full-nav overlay — the
 * web-only trailing "More…" entry was dropped in favor of the same top-of-
 * column toggle used by tablet portrait/landscape.
 *
 * - < 600px: phone (4 cols, no rail — see Footer.vue bottom tab bar)
 * - 600-959px: iPad portrait (5 cols, compact vertical rail)
 * - 960-1099px: iPad landscape (6 cols, compact vertical rail)
 * - >= 1100px: web (6 cols, expanded vertical rail)
 *
 * Every tier's full nav (all 19 items) is ALWAYS rendered as an overlay
 * (dimmed scrim + floating drawer) — there is no more "persistent full grid"
 * tier.
 */
export function useNavGridLayout(windowWidth: Ref<number> | ComputedRef<number>): ComputedRef<NavGridLayout> {
  return computed<NavGridLayout>(() => {
    const width = windowWidth.value

    if (width < 600) return TIERS.phone
    if (width < 960) return TIERS.tabletPortrait
    if (width < 1100) return TIERS.tabletLandscape

    return TIERS.web
  })
}
